import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { NewFileUpload } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';
import { LeaseService } from '../service/leaseService';
import { LeaseFormStore } from './leaseFormStore';
import { LeaseRow } from '../util/types';

export class LeaseStore {
  globalStore: GlobalStore;
  private leaseService: LeaseService;

  loanId: number | null;
  propertyId: number | null;
  taskId: number | null;
  leases: LeaseRow[];
  processingStatus: string;
  errorMessage: string | null;
  unitCount: number | null;
  files: NewFileUpload[] = [];
  leaseFormStore: LeaseFormStore;
  leasePreviewData: any = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.leaseService = new LeaseService();
    this.leaseFormStore = new LeaseFormStore(globalStore);
    this.loanId = null;
    this.propertyId = null;
    this.taskId = null;
    this.leases = [];
    this.processingStatus = null;
    this.errorMessage = null;
    this.unitCount = null;

    makeObservable(this, {
      loanId: observable,
      propertyId: observable,
      taskId: observable,
      leases: observable,
      processingStatus: observable,
      errorMessage: observable,
      unitCount: observable,
      files: observable,
      leasePreviewData: observable,
      getLeaseInfo: flow,
      saveLease: flow,
      uploadFiles: flow,
      downloadLease: flow,
      deleteLease: flow,
      previewLease: flow,
      setFiles: action,
      setContext: action,
      loadEditForm: action,
    });
  }

  loadEditForm(lease: LeaseRow) {
    this.leaseFormStore.reset();
    this.leaseFormStore.loadForm({
      unitNumber: lease.unitNumber ?? '',
      tenantName: lease.tenantName ?? '',
      leaseType: lease.leaseType ?? '',
      startDate: lease.startDate ?? '',
      endDate: lease.endDate ?? '',
      monthlyRent: lease.monthlyRent != null ? String(lease.monthlyRent) : '',
      marketRent: lease.marketRent != null ? String(lease.marketRent) : '',
      vacant: lease.vacant ?? false,
      sectionEightFourTwo: lease.sectionEightFourTwo ? 'true' : 'false',
    });
  }

  setContext(loanId: number, propertyId: number, taskId: number) {
    this.loanId = loanId;
    this.propertyId = propertyId;
    this.taskId = taskId;
  }

  setFiles(files: NewFileUpload[]) {
    this.files = files;
  }

  *getLeaseInfo(loanId: number, taskId: number) {
    this.loanId = loanId;
    this.taskId = taskId;
    try {
      const response = yield this.leaseService.getLeaseInfo(loanId, this.propertyId);
      const data = response?.data?.data;
      this.leases = data?.leases ?? [];
      this.processingStatus = data?.processingStatus ?? null;
      this.errorMessage = data?.errorMessage ?? null;
      this.unitCount = data?.unitCount ?? null;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching lease information',
      });
    }
  }

  *uploadFiles(loanId: number, propertyId: number, taskId: number) {
    if (this.files.length === 0) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please upload at least one document before submitting.',
      });
      throw new Error();
    }

    try {
      yield this.leaseService.uploadLeases(
        this.files.map(d => d.file),
        loanId,
        propertyId,
        taskId
      );
      this.files = [];
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Lease documents uploaded successfully.',
      });
      yield this.getLeaseInfo(loanId, taskId);
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while uploading lease documents.',
      });
      throw new Error();
    }
  }

  *saveLease(editingLeaseId?: number) {
    if (!this.leaseFormStore.runFormValidationWithMessage()) {
      throw new Error();
    }

    const values = this.leaseFormStore.getFormValues();
    const payload: Partial<LeaseRow> = {
      leaseId: editingLeaseId ?? undefined,
      loanId: this.loanId ?? undefined,
      propertyId: this.propertyId ?? undefined,
      taskId: this.taskId ?? undefined,
      unitNumber: values.unitNumber,
      tenantName: values.tenantName,
      leaseType: values.leaseType,
      startDate: values.startDate,
      endDate: values.endDate,
      monthlyRent: values.monthlyRent !== '' ? Number(values.monthlyRent) : null,
      marketRent: values.marketRent !== '' ? Number(values.marketRent) : null,
      vacant: values.vacant === true,
      sectionEightFourTwo: values.sectionEightFourTwo === 'true',
    };

    try {
      yield this.leaseService.saveLease(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Lease saved successfully.',
      });
      if (this.loanId && this.taskId) {
        yield this.getLeaseInfo(this.loanId, this.taskId);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the lease',
      });
      throw new Error();
    }
  }

  *downloadLease(leaseId: number) {
    try {
      const response = yield this.leaseService.downloadLease(leaseId);
      downloadDocument(response?.data, response?.headers, 'download');
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while downloading the lease document.',
      });
    }
  }

  *deleteLease(leaseId: number) {
    try {
      yield this.leaseService.deleteLease(leaseId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Lease deleted successfully.',
      });
      if (this.loanId && this.taskId) {
        yield this.getLeaseInfo(this.loanId, this.taskId);
      }
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while deleting the lease.',
      });
    }
  }

  *previewLease(leaseId: number) {
    try {
      this.leasePreviewData = null;
      const response = yield this.leaseService.downloadLease(leaseId);
      this.leasePreviewData = response;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the document preview.',
      });
    }
  }
}
